<template>
  <v-layout row wrap class="pa-2">
    <v-flex xs12 sm12 md6 pr-2 d-flex class="px-2 py-2">
      <v-card class="elevation-2" style="max-width: 100%;">
        <v-card-title>
          <span style="font-size: 12px;" class="primary--text"
            >Total Suppliers Payables</span
          >
          <v-tooltip right>
            <v-icon small color="#ccc" slot="activator" class="pl-2"
              >help</v-icon
            >
            <span
              >Current and overdue amount that you're yet to pay to your
              supplier</span
            >
          </v-tooltip>
          <span class="pl-4">
            Total unpaid amount:
            <b class="pl-2">{{ inCurrency(currencyCode, payablesTotal) }}</b>
          </span>
        </v-card-title>

        <v-progress-linear
          class="mt-0"
          :background-color="currentOverdueColor('overdue')"
          :color="currentOverdueColor('current')"
          height="2"
          v-model="payablesValue"
        />

        <v-card-text class="pt-0">
          <v-layout class="py-0 pb-2" justify-center>
            <v-flex xs12 px-4 class="text-xs-center">
              <span :style="'color: ' + currentOverdueColor('current') + ';'"
                >CURRENT</span
              >
              <br />
              <span style="color: #333;" class="subheading">{{
                inCurrency(currencyCode, payablesCurrent)
              }}</span>
            </v-flex>
            <v-divider vertical></v-divider>
            <v-flex xs12 px-4 class="text-xs-center">
              <span :style="'color: ' + currentOverdueColor('overdue') + ';'"
                >OVERDUE</span
              >
              <br />
              <span style="color: #333;" class="subheading">{{
                inCurrency(currencyCode, payablesOverdue)
              }}</span>
            </v-flex>
          </v-layout>

          <v-list two-line dense ripple>
            <template v-for="(item, index) in sortedCompanyPayables">
              <v-list-tile
                :key="'supp' + index"
                light
                ripple
                :to="`/company_accounts/suppliers_accounts?company_id=${item.id}`"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title class="grey--text-darken2">{{
                    item.name
                  }}</v-list-tile-sub-title>
                </v-list-tile-content>

                <v-list-tile-action>
                  <span>
                    <strong
                      class="caption font-weight-bold"
                      style="position: relative; top: 3px;"
                      >{{
                        inCurrency(
                          currencyCode,
                          item.overdueAmount + item.currentAmount
                        )
                      }}</strong
                    >
                    <span
                      class="caption"
                      v-if="item.overdueAmount > 0"
                      :style="
                        'margin-left:8px; position:relative; top: 3px;  color:' +
                        currentOverdueColor('overdue')
                      "
                      >({{
                        inCurrency(currencyCode, item.overdueAmount)
                      }})</span
                    >
                  </span>

                  <v-label>
                    <span style="position: relative; top: -4px !important;">
                      Overdue {{ item.overdueAmount > 0 ? "since:" : "on:" }}
                      <strong>{{ timeToDate(item.olderDueDate) }}</strong>
                    </span>
                  </v-label>
                </v-list-tile-action>
              </v-list-tile>
              <v-flex
                :key="item.name"
                class="px-3"
                v-if="index < Object.keys(sortedCompanyPayables).length - 1"
              >
                <v-divider></v-divider>
              </v-flex>

              <v-layout row justify-center :key="item.name" v-else>
                <v-btn
                  small
                  flat
                  @click="displayAllPayables = !displayAllPayables"
                  v-if="Object.keys(companyPayables).length > nbLines"
                >
                  {{
                    displayAllPayables
                      ? "Hide"
                      : "Show " +
                        (Object.keys(companyPayables).length - nbLines) +
                        " more"
                  }}
                  <v-icon>{{
                    displayAllPayables
                      ? "keyboard_arrow_up"
                      : "keyboard_arrow_down"
                  }}</v-icon>
                </v-btn>
              </v-layout>
            </template>
          </v-list>
        </v-card-text>
      </v-card>
    </v-flex>

    <v-flex xs12 sm12 md6 pl-2 d-flex class="px-2 py-2">
      <v-card class="elevation-2" style="max-width: 100%;">
        <v-card-title>
          <span style="font-size: 12px;" class="primary--text"
            >Total Customers Receivables</span
          >
          <v-tooltip right>
            <v-icon small color="#ccc" slot="activator" class="pl-2"
              >help</v-icon
            >
            <span
              >Current and overdue amount that you're yet to receive from
              customers</span
            >
          </v-tooltip>
          <span class="pl-4">
            Total unpaid amount:
            <b class="pl-2">{{ inCurrency(currencyCode, receivablesTotal) }}</b>
          </span>
        </v-card-title>
        <v-progress-linear
          class="mt-0"
          :background-color="currentOverdueColor('overdue')"
          :color="currentOverdueColor('current')"
          :value="receivablesValue"
          height="2"
        />

        <v-card-text class="pt-0">
          <v-layout class="py-0 pb-2">
            <v-flex xs12 px-4 class="text-xs-center">
              <span :style="'color: ' + currentOverdueColor('current') + ';'"
                >CURRENT</span
              >
              <br />
              <span style="color: #333;" class="subheading">{{
                inCurrency(currencyCode, receivablesCurrent)
              }}</span>
            </v-flex>
            <v-divider vertical></v-divider>
            <v-flex xs12 px-4 class="text-xs-center">
              <span :style="'color: ' + currentOverdueColor('overdue') + ';'"
                >OVERDUE</span
              >
              <br />
              <span style="color: #333;" class="subheading">{{
                inCurrency(currencyCode, receivablesOverdue)
              }}</span>
            </v-flex>
          </v-layout>
          <v-list dense two-line ripple>
            <template v-for="(item, index) in sortedCompanyReceivables">
              <v-list-tile
                :key="'cust' + index"
                light
                ripple
                :to="`/company_accounts/customers_accounts?company_id=${item.id}`"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title class="grey--text-darken2">{{
                    item.name
                  }}</v-list-tile-sub-title>
                </v-list-tile-content>

                <v-list-tile-action>
                  <span>
                    <strong
                      class="caption font-weight-bold"
                      style="position: relative; top: 3px;"
                      >{{
                        inCurrency(
                          currencyCode,
                          item.overdueAmount + item.currentAmount
                        )
                      }}</strong
                    >
                    <span
                      class="caption"
                      v-if="item.overdueAmount > 0"
                      :style="
                        'margin-left:8px; position:relative; top: 3px;  color:' +
                        currentOverdueColor('overdue')
                      "
                      >({{
                        inCurrency(currencyCode, item.overdueAmount)
                      }})</span
                    >
                  </span>

                  <v-label>
                    <span style="position: relative; top: -4px !important;">
                      Overdue {{ item.overdueAmount > 0 ? "since:" : "on:" }}
                      <strong>{{ timeToDate(item.olderDueDate) }}</strong>
                    </span>
                  </v-label>
                </v-list-tile-action>
              </v-list-tile>
              <v-flex
                :key="item.name"
                class="px-3"
                v-if="index < Object.keys(companyReceivables).length - 1"
              >
                <v-divider></v-divider>
              </v-flex>
              <v-layout row justify-center :key="item.name" v-else>
                <v-btn
                  small
                  flat
                  @click="displayAllReceivables = !displayAllReceivables"
                  v-if="Object.keys(sortedCompanyReceivables).length > nbLines"
                >
                  {{
                    displayAllPayables
                      ? "Hide"
                      : "Show " +
                        (Object.keys(companyPayables).length - nbLines) +
                        " more"
                  }}
                  <v-icon>{{
                    displayAllPayables
                      ? "keyboard_arrow_up"
                      : "keyboard_arrow_down"
                  }}</v-icon>
                </v-btn>
              </v-layout>
            </template>
          </v-list>
        </v-card-text>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapGetters } from "vuex";
import currency from "../../../mixins/currency";
import date from "../../../mixins/date";
import accountingFilters from "../../../mixins/accountingFilters";
import INVOICES_INDEX from "../../../graphql/bank_invoices/index.gql";

export default {
  name: "accountingReceivablesPayables",
  mixins: [accountingFilters, currency, date],
  data() {
    return {
      nbLines: 5,
      displayAllReceivables: false,
      displayAllPayables: false,
    };
  },
  computed: {
    ...mapGetters("commonStore", [
      "notReconciliatedBankInvoices",
      "currencies",
    ]),
    receivables() {
      var ret = this.notReconciliatedBankInvoices.filter(
        (e) =>
          e.currency_id === this.currencyId && e.operation_type === "credit"
      );
      if (this.subsidiaryId)
        ret = ret.filter((e) => e.subsidiary_company_id === this.subsidiaryId);
      ret.forEach((e) => {
        e.overdue = this.isDateInThePast(e.expected_payment_date);
      });
      return ret;
    },
    payables() {
      var ret = this.notReconciliatedBankInvoices.filter(
        (e) => e.currency_id === this.currencyId && e.operation_type === "debit"
      );
      if (this.subsidiaryId)
        ret = ret.filter((e) => e.subsidiary_company_id === this.subsidiaryId);
      ret.forEach((e) => {
        e.overdue = this.isDateInThePast(e.expected_payment_date);
      });
      return ret;
    },
    receivablesCurrent() {
      var tot = 0;
      this.receivables.forEach((element) => {
        if (!element.overdue)
          tot += element.amount - element.reconciliated_amount;
      });
      return tot.toFixed(2);
    },
    receivablesOverdue() {
      var tot = 0;
      this.receivables.forEach((element) => {
        if (element.overdue)
          tot += element.amount - element.reconciliated_amount;
      });
      return tot.toFixed(2);
    },
    payablesCurrent() {
      var tot = 0;
      this.payables.forEach((element) => {
        if (!element.overdue)
          tot += element.amount - element.reconciliated_amount;
      });
      return tot.toFixed(2);
    },
    payablesOverdue() {
      var tot = 0;
      this.payables.forEach((element) => {
        if (element.overdue)
          tot += element.amount - element.reconciliated_amount;
      });
      return parseFloat(tot).toFixed(2);
    },
    receivablesTotal() {
      var tot =
        parseFloat(this.receivablesCurrent) +
        parseFloat(this.receivablesOverdue);
      return parseFloat(tot).toFixed(2);
    },
    payablesTotal() {
      var tot =
        parseFloat(this.payablesCurrent) + parseFloat(this.payablesOverdue);
      return parseFloat(tot).toFixed(2);
    },
    receivablesValue() {
      if (this.receivablesTotal == 0) return 0;
      else return (this.receivablesCurrent / this.receivablesTotal) * 100;
    },
    payablesValue() {
      if (this.payablesTotal == 0) return 0;
      else return (this.payablesCurrent / this.payablesTotal) * 100;
    },
    companyReceivables() {
      var ret = {};
      for (var i = 0; i < this.receivables.length; i++) {
        let act = this.receivables[i];
        let act_company_id = act.company_id;
        if (!ret[act_company_id])
          ret[act_company_id] = this.initCompanyEntry(act.company);
        let currentEntry = ret[act_company_id];
        if (act.overdue)
          currentEntry.overdueAmount += act.amount - act.reconciliated_amount;
        else
          currentEntry.currentAmount += act.amount - act.reconciliated_amount;
        currentEntry.olderDueDate = this.olderDate(
          currentEntry.olderDueDate,
          act.expected_payment_date
        );
      }
      return ret;
    },
    sortedCompanyReceivables() {
      var ret = Object.values(this.companyReceivables).sort(this.entryComparer);
      if (!this.displayAllReceivables) ret = ret.slice(0, this.nbLines);
      return ret;
    },
    companyPayables() {
      var ret = {};
      for (var i = 0; i < this.payables.length; i++) {
        let act = this.payables[i];
        let act_company_id = act.company_id;
        if (!ret[act_company_id])
          ret[act_company_id] = this.initCompanyEntry(act.company);
        let currentEntry = ret[act_company_id];
        if (act.overdue)
          currentEntry.overdueAmount += act.amount - act.reconciliated_amount;
        else
          currentEntry.currentAmount += act.amount - act.reconciliated_amount;
        currentEntry.olderDueDate = this.olderDate(
          currentEntry.olderDueDate,
          act.expected_payment_date
        );
      }
      return ret;
    },
    sortedCompanyPayables() {
      var ret = Object.values(this.companyPayables).sort(this.entryComparer);
      if (!this.displayAllPayables) ret = ret.slice(0, this.nbLines);
      return ret;
    },
  },
  methods: {
    goToCompanyAccounts(company_id) {
      this.$router.push({
        name: "company_accounts",
        params: { company_id: company_id },
      });
    },
    entryComparer(a, b) {
      return this.olderDate(a.olderDueDate, b.olderDueDate) === a.olderDueDate
        ? -1
        : 1;
    },
    initCompanyEntry(company) {
      return {
        id: company.id,
        name: company.name,
        currentAmount: 0,
        overdueAmount: 0,
        olderDueDate: "",
      };
    },
    currentOverdueColor(arg = "-") {
      switch (arg) {
        case "current":
          return "#268ddd";
        case "overdue":
          return "#ff3200";
        default:
          return "#f4f4f4";
      }
    },
    fetchInvoices() {
      this.fetchAndSubscribe(
        INVOICES_INDEX,
        {
          filter: "reconciliation_status != done",
        },
        "commonStore",
        "notReconciliatedBankInvoices"
      );
    },
  },
  created() {
    this.fetchInvoices();
  },
  beforeDestroy() {
    this.unsubscribe("notReconciliatedBankInvoices");
  },
};
</script>
<style>
.v-list__tile__action--stack {
  margin: 0;
  padding: 0;
}
</style>
